import { countAncestors, maxAncestors, Permission, sexLabel, type PedigreeNode } from '@pedigreesmart/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Documenti } from '../components/Documenti.js'
import { useSession, useTerm } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './animals.css'

/**
 * La scheda di un soggetto.
 *
 * È la schermata in cui il motore delle specie si vede lavorare: **niente qui
 * dentro sa cosa sia un cane**. I campi specifici — microchip, marca
 * auricolare, UELN — arrivano dalla definizione della specie che il server ha
 * mandato nel contesto di sessione, e le parole («fattrice», «cucciolata»)
 * dalla sua terminologia. Aggiungere una specie non tocca questo file.
 */
export function AnimalDetail({
  id,
  onTorna,
  onModifica,
}: {
  id: number
  onTorna: () => void
  onModifica?: ((id: number) => void) | undefined
}) {
  const { resources, species } = useSession()

  const animale = useQuery({
    queryKey: ['animal', id],
    queryFn: ({ signal }) => resources.animals.get(id, signal),
  })

  if (animale.isPending) {
    return <p className="messaggio">Caricamento…</p>
  }

  if (animale.isError) {
    return (
      <section>
        <button type="button" data-variante="piatto" onClick={onTorna}>
          ← Torna all'elenco
        </button>
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere questa scheda.
        </p>
      </section>
    )
  }

  const a = animale.data

  return (
    <section aria-labelledby="titolo-scheda">
      <button type="button" data-variante="piatto" onClick={onTorna}>
        ← Torna all'elenco
      </button>

      <header className="scheda__testa">
        <div>
          <h1 id="titolo-scheda">{a.full_name ?? a.name}</h1>
          {a.registry_number && <p className="codice scheda__registro">{a.registry_number}</p>}
        </div>
        <div className="elenco__azioni">
          {/* Un soggetto deceduto si dice subito e non in fondo alla scheda:
              cambia il senso di tutto quello che segue. */}
          {a.death_date && <p className="etichetta etichetta--grave">Deceduto</p>}
          {onModifica && (
            <button
              type="button"
              data-variante="secondario"
              onClick={() => {
                onModifica(id)
              }}
            >
              Modifica
            </button>
          )}
          <Certificato id={id} iscritto={Boolean(a.registry_number)} />
        </div>
      </header>

      <div className="scheda__griglia">
        <Riquadro titolo="Dati">
          <Dato voce="Nome" valore={a.name} />
          <Dato voce="Sesso" valore={sexLabel(a.sex, species)} />
          <Dato voce="Nascita" valore={data(a.birth_date)} />
          {a.death_date && <Dato voce="Decesso" valore={data(a.death_date)} />}
          <Dato voce="Razza" valore={a.breed?.name ?? null} />
          <Dato voce="Colore" valore={a.color} />
        </Riquadro>

        <AttributiDiSpecie valori={a.attributes ?? {}} />

        <Riquadro titolo="Persone">
          <Dato voce="Proprietario" valore={a.current_owner?.display_name ?? null} />
          <Dato voce={useTerm('breeder')} valore={a.breeder?.display_name ?? null} />
        </Riquadro>
      </div>

      <Vetrina
        id={id}
        inVendita={a.for_sale}
        pubblicabile={Boolean(a.registry_number) && !a.death_date && !a.deregistered_on}
      />

      <Sanita id={id} />

      <Documenti ownerType="animal" ownerId={id} />

      <Eventi id={id} />

      {a.litter && <Cucciolata litter={a.litter} />}

      <Intestazioni id={id} />

      <Genealogia id={id} coefficiente={a.inbreeding_coefficient ?? null} />
    </section>
  )
}

/**
 * Gli attributi che dipendono dalla specie.
 *
 * L'etichetta la sceglie l'associazione quando definisce la specie; noi non
 * ne conosciamo nessuna. Un attributo definito ma non compilato si mostra
 * comunque, vuoto: un campo assente dalla scheda sembra un campo che non
 * esiste, e chi lo cerca continua a cercarlo.
 */
function AttributiDiSpecie({ valori }: { valori: Record<string, unknown> }) {
  const { species } = useSession()

  if (!species || species.attributes.length === 0) return null

  return (
    <Riquadro titolo="Dati di specie">
      {species.attributes.map((attributo) => (
        <Dato
          key={attributo.key}
          voce={attributo.label}
          valore={testo(valori[attributo.key])}
          unita={attributo.unit}
          codice={attributo.searchable}
        />
      ))}
    </Riquadro>
  )
}

/**
 * La genealogia, e il coefficiente che ne discende.
 *
 * L'albero si chiede a parte dalla scheda: risalire la discendenza costa, e
 * chi apre un soggetto per correggere il colore non deve pagare quel costo.
 */
function Genealogia({ id, coefficiente }: { id: number; coefficiente: number | null }) {
  const { resources } = useSession()
  const dam = useTerm('dam')
  const sire = useTerm('sire')

  const albero = useQuery({
    queryKey: ['pedigree', id],
    queryFn: ({ signal }) => resources.animals.pedigree(id, undefined, signal),
  })

  if (albero.isPending) return <p className="messaggio">Caricamento della genealogia…</p>

  if (albero.isError) {
    return (
      <p className="messaggio">
        La genealogia non è disponibile al momento.
      </p>
    )
  }

  const { root, generations, common_ancestors: antenati, truncated } = albero.data
  const presenti = countAncestors(root)
  const massimo = maxAncestors(generations)

  return (
    <section className="genealogia" aria-labelledby="titolo-genealogia">
      <div className="elenco__testa">
        <h2 id="titolo-genealogia">Genealogia</h2>
        {/* «12 antenati su 30» dice quanto è completo l'albero meglio di
            qualunque disegno: è il numero che un allevatore guarda per primo. */}
        <p className="elenco__conteggio">
          {presenti} {presenti === 1 ? 'antenato' : 'antenati'} su {massimo} possibili
          {truncated && ' — l\'albero continua oltre'}
        </p>
      </div>

      {presenti === 0 && (
        <p className="messaggio">
          Nessun antenato registrato: è un capostipite, oppure la genealogia non è
          ancora stata inserita.
        </p>
      )}

      {presenti > 0 && (
        <div className="albero">
          <Ramo nodo={root.sire} ruolo={sire} />
          <Ramo nodo={root.dam} ruolo={dam} />
        </div>
      )}

      {null !== coefficiente && (
        <div className="consanguineita">
          <h3>Consanguineità</h3>
          <p className="consanguineita__valore">
            {(coefficiente * 100).toLocaleString('it-IT', { maximumFractionDigits: 2 })}%
          </p>
          {antenati.length > 0 && (
            <>
              <p className="consanguineita__spiega">
                {antenati.length === 1
                  ? 'Un antenato compare su entrambi i rami:'
                  : `${antenati.length} antenati compaiono su entrambi i rami:`}
              </p>
              <ul className="consanguineita__antenati">
                {antenati.map((antenato) => (
                  <li key={antenato.id}>
                    <strong>{antenato.name ?? `#${antenato.id}`}</strong>{' '}
                    <span className="riga__secondario">
                      contribuisce per{' '}
                      {(antenato.contribution * 100).toLocaleString('it-IT', {
                        maximumFractionDigits: 2,
                      })}
                      %
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * Un ramo dell'albero, in profondità.
 *
 * Un antenato **senza id** non è un errore: è un soggetto dichiarato sul
 * pedigree cartaceo ma non presente nel nostro registro. Si mostra col nome e
 * senza collegamento, perché esiste come informazione e non come scheda.
 */
function Ramo({ nodo, ruolo }: { nodo: PedigreeNode | null | undefined; ruolo: string }) {
  if (!nodo) {
    return (
      <div className="albero__nodo albero__nodo--vuoto">
        <span className="albero__ruolo">{ruolo}</span>
        <span className="riga__secondario">non indicato</span>
      </div>
    )
  }

  return (
    <div className="albero__nodo">
      <span className="albero__ruolo">{ruolo}</span>
      <span className="albero__nome">{nodo.full_name ?? nodo.name}</span>
      {nodo.registry_number && <span className="codice riga__secondario">{nodo.registry_number}</span>}

      {(nodo.dam ?? nodo.sire) && (
        <div className="albero__figli">
          <Ramo nodo={nodo.sire} ruolo="Padre" />
          <Ramo nodo={nodo.dam} ruolo="Madre" />
        </div>
      )}
    </div>
  )
}

/** Le categorie di referto, con le parole che si usano in segreteria. */
const CATEGORIE: Record<string, string> = {
  visit: 'Visita',
  vaccination: 'Vaccinazione',
  test: 'Esame',
  treatment: 'Trattamento',
  genetic: 'Test genetico',
}

/**
 * I referti sanitari.
 *
 * Non è un dettaglio clinico: per molte razze i risultati di displasia e dei
 * test genetici sono **condizione di iscrizione** e compaiono sul certificato.
 * Chi compra un cucciolo li chiede prima di tutto il resto.
 *
 * Il modulo per aggiungerne uno sta qui e non in una schermata a parte perché
 * un referto arriva sempre riferito a un soggetto: si registra guardando la
 * scheda di quello, non cercandolo da un elenco.
 */
function Sanita({ id }: { id: number }) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [apre, setApre] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  const referti = useQuery({
    queryKey: ['health', id],
    queryFn: ({ signal }) => resources.records.health(id, signal),
  })

  const aggiungi = useMutation({
    mutationFn: (corpo: Parameters<typeof resources.records.addHealth>[0]) =>
      resources.records.addHealth(corpo),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: () => {
      setApre(false)
      void cache.invalidateQueries({ queryKey: ['health', id] })
    },
    onError: () => {
      setErrore('Non è stato possibile registrare il referto.')
    },
  })

  return (
    <section className="storico" aria-labelledby="titolo-sanita">
      <div className="elenco__testa">
        <h2 id="titolo-sanita">Referti sanitari</h2>
        {!apre && (
          <button
            type="button"
            data-variante="secondario"
            onClick={() => {
              setApre(true)
            }}
          >
            Aggiungi
          </button>
        )}
      </div>

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      {apre && (
        <form
          className="modulo modulo--breve"
          onSubmit={(evento) => {
            evento.preventDefault()

            const campi = new FormData(evento.currentTarget)
            const testo = (chiave: string) => ((campi.get(chiave) as string | null) ?? '').trim()

            aggiungi.mutate({
              animal: `/api/v1/animals/${String(id)}`,
              category: testo('category'),
              ...(testo('description') ? { description: testo('description') } : {}),
              ...(testo('performed_on') ? { performed_on: testo('performed_on') } : {}),
              ...(testo('result') ? { result: testo('result') } : {}),
              ...(testo('provider') ? { provider: testo('provider') } : {}),
            })
          }}
        >
          <fieldset>
            <legend>Nuovo referto</legend>
            <label className="modulo__campo">
              <span>Categoria</span>
              {/* Una tendina e non un campo libero: il valore è un insieme
                  chiuso, e il rifiuto dice «non è una scelta valida» senza
                  elencare quali lo siano. */}
              <select name="category" defaultValue="test">
                {Object.entries(CATEGORIE).map(([valore, etichetta]) => (
                  <option key={valore} value={valore}>
                    {etichetta}
                  </option>
                ))}
              </select>
            </label>
            <label className="modulo__campo">
              <span>Descrizione</span>
              <input type="text" name="description" placeholder="Displasia dell'anca" />
            </label>
            <label className="modulo__campo">
              <span>Esito</span>
              <input type="text" name="result" placeholder="A" />
            </label>
            <label className="modulo__campo">
              <span>Eseguito il</span>
              <input type="date" name="performed_on" />
            </label>
            <label className="modulo__campo">
              <span>Eseguito da</span>
              <input type="text" name="provider" />
            </label>
            <div className="modulo__azioni">
              <button
                type="button"
                data-variante="piatto"
                onClick={() => {
                  setApre(false)
                }}
              >
                Annulla
              </button>
              <button type="submit" disabled={aggiungi.isPending}>
                {aggiungi.isPending ? 'Salvataggio…' : 'Registra il referto'}
              </button>
            </div>
          </fieldset>
        </form>
      )}

      {referti.data?.member.length === 0 && !apre && (
        <p className="messaggio">Nessun referto registrato.</p>
      )}

      {referti.data && referti.data.member.length > 0 && (
        <ol className="storico__righe">
          {referti.data.member.map((referto) => (
            <li key={referto.id} className="storico__riga">
              <span className="riga__nome">
                {referto.description ?? referto.type?.label ?? CATEGORIE[referto.category] ?? referto.category}
              </span>
              <span className="storico__periodo">{data(referto.performed_on) ?? '—'}</span>
              <span className="riga__secondario">
                {referto.result ? `Esito: ${referto.result}` : (CATEGORIE[referto.category] ?? referto.category)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}

/**
 * Le partecipazioni a esposizioni, prove e controlli.
 *
 * Il tipo arriva col suo nome («Esposizione»), non come riferimento: fino al 9
 * agosto era un IRI nudo e qui sarebbe comparso `/api/v1/lookups/1`.
 */
function Eventi({ id }: { id: number }) {
  const { resources } = useSession()

  const eventi = useQuery({
    queryKey: ['events', id],
    queryFn: ({ signal }) => resources.records.events(id, signal),
  })

  if (!eventi.data || eventi.data.member.length === 0) return null

  return (
    <section className="storico" aria-labelledby="titolo-eventi">
      <h2 id="titolo-eventi">Esposizioni e prove</h2>
      <ol className="storico__righe">
        {eventi.data.member.map((evento) => (
          <li key={evento.id} className="storico__riga">
            <span className="riga__nome">{evento.title}</span>
            <span className="storico__periodo">{data(evento.held_on) ?? '—'}</span>
            <span className="riga__secondario">
              {[evento.type?.label, evento.place, evento.result].filter(Boolean).join(' · ')}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/**
 * Lo storico delle intestazioni.
 *
 * Per un libro genealogico non è un dettaglio ma **il dato legale**: la scheda
 * mostrava solo il proprietario attuale, e chi doveva ricostruire un passaggio
 * contestato non aveva dove guardare.
 *
 * Le righe arrivano già in ordine dal più recente (l'ordinamento è della
 * risorsa, non un parametro che potremmo passare a vuoto), e ciascuna dice
 * **perché** l'intestazione è cambiata: nascita, vendita, trasferimento.
 */
function Intestazioni({ id }: { id: number }) {
  const { resources } = useSession()

  const storico = useQuery({
    queryKey: ['ownerships', id],
    queryFn: ({ signal }) => resources.ownerships.byAnimal(id, signal),
  })

  if (storico.isPending || storico.isError) return null
  if (storico.data.member.length === 0) return null

  return (
    <section className="storico" aria-labelledby="titolo-storico">
      <h2 id="titolo-storico">Intestazioni</h2>
      <ol className="storico__righe">
        {storico.data.member.map((riga) => (
          <li key={riga.id} className="storico__riga" data-attuale={riga.current ? 'si' : undefined}>
            <span className="riga__nome">{riga.owner?.display_name ?? '—'}</span>
            <span className="storico__periodo">
              dal {data(riga.started_on) ?? '—'}
              {riga.ended_on ? ` al ${data(riga.ended_on) ?? '—'}` : riga.current ? ' · in corso' : ''}
            </span>
            {riga.reason && <span className="riga__secondario">{motivo(riga.reason)}</span>}
          </li>
        ))}
      </ol>
    </section>
  )
}

/**
 * Il motivo del passaggio, in parole.
 *
 * I codici li scrive il backend quando registra una denuncia. Un codice non
 * previsto si mostra com'è invece di sparire: dice comunque qualcosa, e
 * nasconderlo farebbe sembrare che il motivo non ci sia.
 */
function motivo(codice: string): string {
  switch (codice) {
    case 'birth':
      return 'Nascita'
    case 'transfer':
      return 'Passaggio di proprietà'
    case 'sale':
      return 'Vendita'
    case 'import':
      return 'Importazione'
    default:
      return codice
  }
}

/**
 * La cucciolata di provenienza.
 *
 * È il collegamento fra un soggetto e i suoi fratelli di nidiata, e senza di
 * esso la scheda racconta un animale nato da solo.
 */
function Cucciolata({
  litter,
}: {
  litter: { id?: number; birth_date?: string | null; registered_count?: number }
}) {
  return (
    <section className="storico" aria-labelledby="titolo-cucciolata">
      <h2 id="titolo-cucciolata">{useTerm('litter')}</h2>
      <p>
        Nata il {data(litter.birth_date) ?? '—'}
        {typeof litter.registered_count === 'number' &&
          ` · ${String(litter.registered_count)} iscritti`}
      </p>
    </section>
  )
}

/**
 * Il certificato genealogico.
 *
 * Il pulsante c'è solo per i soggetti iscritti al registro: il certificato di
 * un soggetto senza numero non esiste, e il server risponde `404`. Mostrarlo
 * comunque significherebbe offrire un'azione che fallisce sempre — e un `404`
 * su un pulsante sembra un guasto, non una regola.
 *
 * Il file non si apre con un collegamento: la richiesta vuole il token, che
 * un `<a href>` non porta. Arriva quindi come richiesta autenticata e viene
 * salvato con il nome che gli dà il server, cioè con il numero di registro
 * dentro.
 */
function Certificato({ id, iscritto }: { id: number; iscritto: boolean }) {
  const { resources } = useSession()
  const [errore, setErrore] = useState<string | null>(null)

  const scarica = useMutation({
    mutationFn: () => resources.animals.certificate(id),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: ({ blob, filename }) => {
      const indirizzo = URL.createObjectURL(blob)
      const collegamento = document.createElement('a')

      collegamento.href = indirizzo
      collegamento.download = filename
      collegamento.click()
      URL.revokeObjectURL(indirizzo)
    },
    onError: (e: unknown) => {
      setErrore(
        (e as { status?: number }).status === 404
          ? 'Il certificato esiste solo per i soggetti iscritti al registro.'
          : 'Non è stato possibile preparare il certificato.',
      )
    },
  })

  if (!iscritto) return null

  return (
    <>
      <button
        type="button"
        data-variante="secondario"
        disabled={scarica.isPending}
        onClick={() => {
          scarica.mutate()
        }}
      >
        {scarica.isPending ? 'Preparazione…' : 'Certificato (PDF)'}
      </button>
      {errore && (
        <span role="alert" className="modulo__errore">
          {errore}
        </span>
      )}
    </>
  )
}

/**
 * L'interruttore della vetrina pubblica.
 *
 * Sta sulla scheda e non nel modulo di modifica perché **pubblicare è un atto
 * diverso dal correggere un dato**: vuole `listing.manage`, che non tutti
 * quelli che possono scrivere una scheda hanno. Finché il flag viaggiava fra i
 * campi del modulo quel permesso non lo verificava nessuno.
 *
 * Quando il soggetto non è pubblicabile — mai iscritto, radiato, deceduto —
 * l'interruttore non c'è e c'è scritto perché: la bacheca lo scarterebbe
 * comunque, e un comando che sembra funzionare senza effetto è peggio di un
 * comando assente.
 */
function Vetrina({
  id,
  inVendita,
  pubblicabile,
}: {
  id: number
  inVendita: boolean
  pubblicabile: boolean
}) {
  const { resources, can } = useSession()
  const queryClient = useQueryClient()
  const [errore, setErrore] = useState<string | null>(null)

  const cambia = useMutation({
    mutationFn: (valore: boolean) => resources.animals.listing(id, valore),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['animal', id] }),
    onError: (e: unknown) => {
      setErrore(
        (e as { status?: number }).status === 409
          ? 'Questo soggetto non può comparire in vetrina.'
          : 'Non è stato possibile cambiare la vetrina.',
      )
    },
  })

  if (!can(Permission.listingManage)) {
    // Senza il permesso resta comunque visibile lo stato: sapere che un
    // soggetto è pubblicato riguarda anche chi non può cambiarlo.
    return inVendita ? (
      <Riquadro titolo="Vetrina">
        <p className="messaggio">In vendita sulla vetrina pubblica.</p>
      </Riquadro>
    ) : null
  }

  return (
    <Riquadro titolo="Vetrina">
      {pubblicabile ? (
        <label className="modulo__interruttore">
          <input
            type="checkbox"
            checked={inVendita}
            disabled={cambia.isPending}
            onChange={(evento) => {
              cambia.mutate(evento.target.checked)
            }}
          />
          In vendita sulla vetrina pubblica
        </label>
      ) : (
        <p className="messaggio">
          Un soggetto non iscritto a registro, radiato o deceduto non compare in vetrina.
        </p>
      )}
      {errore && (
        <p role="alert" className="modulo__errore">
          {errore}
        </p>
      )}
    </Riquadro>
  )
}

function Riquadro({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <div className="scheda__riquadro">
      <h2>{titolo}</h2>
      <dl>{children}</dl>
    </div>
  )
}

function Dato({
  voce,
  valore,
  unita,
  codice,
}: {
  voce: string
  valore: string | null | undefined
  unita?: string | null | undefined
  codice?: boolean | undefined
}) {
  return (
    <>
      <dt>{voce}</dt>
      <dd className={codice && valore ? 'codice' : undefined}>
        {valore ? `${valore}${unita ? ` ${unita}` : ''}` : '—'}
      </dd>
    </>
  )
}

/**
 * Un valore di attributo, come parola.
 *
 * Gli attributi di specie sono `unknown` per costruzione — il loro tipo lo
 * dichiara l'associazione, non noi — quindi qui si trattano solo le forme che
 * sappiamo scrivere. Un oggetto o un elenco finirebbe come `[object Object]`,
 * che sullo schermo di una segreteria è peggio di un trattino.
 */
function testo(valore: unknown): string | null {
  if (valore === null || valore === undefined || valore === '') return null
  if (typeof valore === 'boolean') return valore ? 'Sì' : 'No'
  if (typeof valore === 'string') return valore
  if (typeof valore === 'number') return valore.toLocaleString('it-IT')

  return null
}

function data(iso: string | null | undefined): string | null {
  if (!iso) return null

  const quando = new Date(iso)

  return Number.isNaN(quando.getTime()) ? null : quando.toLocaleDateString('it-IT')
}
